import { find } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TRADER_ORDER_STATUSES } from 'constants/order.constants';
import {
  AdditionalOrderColumn,
  FilterDefinitionType,
  OrderCompletedBy,
  OrderStatus,
  OrdersTab,
} from 'enums';
import { Orders } from 'features/orders';
import {
  useWhiteTriangle,
  useCurrencies,
  useOrderQueryFilters,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { OrderFilters, FilterDefinition } from 'types';
import { formUtils, orderUtils, requisitesUtils } from 'utils';

export const TraderAllOrders: React.FC = () => {
  const { t: tFilters } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.filters',
  });

  const { isWhiteTriangle, requisitesId } = useWhiteTriangle();
  const { paymentTypes, banks } = useUserContext();
  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();
  useOrderQueryFilters({
    statuses: [OrderStatus.Completed, OrderStatus.Cancelled],
    ...(isWhiteTriangle && { requisitesId }),
  });

  const additionalFilters: FilterDefinition<OrderFilters>[] = useMemo(
    () => [
      orderUtils.getStatusFilterDefinition(TRADER_ORDER_STATUSES),
      {
        label: tFilters('payment_type'),
        name: 'paymentTypeId',
        type: FilterDefinitionType.Select,
        hidden: isWhiteTriangle,
        options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
        getDisplayName: (value: string) =>
          requisitesUtils.getPaymentTypeLabel(
            find(paymentTypes, { id: value })!,
          ),
      },
      {
        label: tFilters('bank'),
        name: 'bankId',
        type: FilterDefinitionType.Select,
        hidden: isWhiteTriangle,
        options: formUtils.getOptions(banks),
        getDisplayName: (value: string) => find(banks, { id: value })?.name,
      },
      {
        label: tFilters('fiat_currency'),
        name: 'fiatCurrencyId',
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        hidden: isWhiteTriangle,
        getDisplayName: getFiatCurrencyCode,
      },
      {
        label: tFilters('customer_bank'),
        name: 'customerBank',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(banks, 'name', 'code'),
        hidden: isWhiteTriangle,
        getDisplayName: (value: string) => find(banks, { code: value })?.name,
      },
      ...(!isWhiteTriangle
        ? requisitesUtils.getRequisitesFieldsFilters<OrderFilters>()
        : []),
      {
        label: tFilters('automation_status'),
        name: 'completedBy',
        type: FilterDefinitionType.Enum,
        enum: OrderCompletedBy,
        getDisplayName: orderUtils.getCompletedByLabel,
      },
    ],
    [
      banks,
      fiatCurrenciesOptions,
      getFiatCurrencyCode,
      paymentTypes,
      tFilters,
      isWhiteTriangle,
    ],
  );

  return (
    <Orders
      tab={OrdersTab.All}
      additionalFilters={additionalFilters}
      additionalColumns={[
        AdditionalOrderColumn.StatusActive,
        AdditionalOrderColumn.TraderCard,
        AdditionalOrderColumn.Customer,
      ]}
    />
  );
};
